import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, updateDoc, addDoc, serverTimestamp, query, where, limit, runTransaction } from "firebase/firestore";
import Spinner from './Spinner.js';
import { ArrowLeft, Plus, Trash2, Search, X, CheckCircle, Save, Send, Loader2, ClipboardCheck, UserCheck, Truck, ArrowDownLeft } from 'lucide-react';
import useClickOutside from './hooks/useClickOutside.js';

const CreateMICPage = ({ db }) => {
  const navigate = useNavigate();
  const { micId } = useParams();

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [fetchingPC, setFetchingPC] = useState(false);
  const [bomDatabase, setBomDatabase] = useState([]);
  const [pcList, setPcList] = useState([]);

  const [formData, setFormData] = useState({
    micDate: new Date().toISOString().split('T')[0],
    micNo: '',
    typeOfMIC: '', // Sale Return, Rental, Purchase, Interbranch Transfer
    referenceNo: '', // PC No, Doc No, PO No
    customerName: '',
    shippingAddress: '',
    warehouse: '',
    components: [],
    preparedBy: '',
    quantityVerifiedBy: '',
    vehicleNumber: '',
    vehicleInTime: '',
    vehicleOutTime: '',
    vehicleDateTime: new Date().toISOString().slice(0, 16),
    verifiedByFinance: '', // Declaration verified by
    declarationDate: new Date().toISOString().split('T')[0],
    micStatus: 'draft',
  });

  // Search state for PC Picker
  const [pcSearchTerm, setPcSearchTerm] = useState('');
  const [pcSuggestions, setPcSuggestions] = useState([]);
  const [showPcSuggestions, setShowPcSuggestions] = useState(false);
  const pcSearchRef = useClickOutside(() => setShowPcSuggestions(false));

  // Search state for manual component entry
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useClickOutside(() => setShowSuggestions(false));

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch BOM Products
        const bomSnap = await getDocs(collection(db, "bom_products"));
        setBomDatabase(bomSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        // Fetch Confirmed Pickups
        const pcQuery = query(collection(db, "pickups"), where("status", "==", "Confirmed"), limit(50));
        const pcSnap = await getDocs(pcQuery);
        setPcList(pcSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        if (micId) {
          const micDoc = await getDoc(doc(db, "inwards", micId));
          if (micDoc.exists()) {
            const data = micDoc.data();
            setFormData({ ...formData, ...data, id: micDoc.id });
            if (data.referenceNo) setPcSearchTerm(data.referenceNo);
          }
        } else {
          // Generate MIC Number
          const generatedMicNo = await getNextMicNumber(db);
          setFormData(prev => ({ ...prev, micNo: generatedMicNo }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [db, micId]);

  const getNextMicNumber = async (db) => {
    const sequencesRef = doc(db, "counters", "mic_sequence");
    try {
      const result = await runTransaction(db, async (transaction) => {
        const sequenceDoc = await transaction.get(sequencesRef);
        let currentCount = 1700;
        if (sequenceDoc.exists() && sequenceDoc.data().count !== undefined) {
          currentCount = sequenceDoc.data().count;
        }
        const nextCount = currentCount + 1;
        transaction.set(sequencesRef, { count: nextCount }, { merge: true });
        return currentCount;
      });
      return `MIC-${result}`;
    } catch (error) {
      console.error("Error getting next MIC number: ", error);
      return "MIC-TEMP";
    }
  };

  const handlePcSearch = (val) => {
    setPcSearchTerm(val);
    if (val.length > 1) {
      const term = val.toLowerCase();
      const matches = pcList.filter(pc => 
        (pc.pcNo || '').toLowerCase().includes(term) ||
        (pc.customerName || pc.consigneeDetails || '').toLowerCase().includes(term)
      ).slice(0, 10);
      setPcSuggestions(matches);
      setShowPcSuggestions(true);
    } else {
      setShowPcSuggestions(false);
    }
  };

  const handlePcSelection = (pc) => {
    setFetchingPC(true);
    setShowPcSuggestions(false);
    setPcSearchTerm(pc.pcNo);
    
    // Extract customer name and address from consigneeDetails or pickupLocation
    // This is a bit fuzzy based on how PC saves it
    const customerName = pc.customerName || pc.consigneeDetails?.split('\n')[0] || '';
    const shippingAddress = pc.pickupLocation || '';

    setFormData(prev => ({
      ...prev,
      referenceNo: pc.pcNo,
      customerName: customerName,
      shippingAddress: shippingAddress,
      warehouse: pc.warehouse || prev.warehouse,
      components: (pc.components || []).map(comp => ({
        id: comp.id || `${comp.sku}-${Date.now()}`,
        sku: comp.sku,
        name: comp.name || comp.description || '',
        newQty: 0,
        rentalQty: 0,
        repairableQty: 0,
        nonRepairableQty: 0,
        weight: comp.weight || 0
      }))
    }));
    setFetchingPC(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleComponentChange = (index, field, value) => {
    const updated = [...formData.components];
    updated[index][field] = field === 'weight' ? parseFloat(value) || 0 : parseInt(value) || 0;
    setFormData(prev => ({ ...prev, components: updated }));
  };

  const deleteComponent = (index) => {
    const updated = formData.components.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, components: updated }));
  };

  const addComponent = (product) => {
    const newComp = {
      id: `${product.sku}-${Date.now()}`,
      sku: product.sku,
      name: product.name,
      newQty: 0,
      rentalQty: 0,
      repairableQty: 0,
      nonRepairableQty: 0,
      weight: product.weight || 0
    };
    setFormData(prev => ({ ...prev, components: [...prev.components, newComp] }));
    setSearchTerm('');
    setShowSuggestions(false);
  };

  const handleSearch = (val) => {
    setSearchTerm(val);
    if (val.length > 1) {
      const term = val.toLowerCase();
      const matches = bomDatabase.filter(p => 
        p.name.toLowerCase().includes(term) || 
        p.sku.toLowerCase().includes(term)
      ).slice(0, 10);
      setFilteredProducts(matches);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const totals = useMemo(() => {
    return formData.components.reduce((acc, c) => {
      acc.newQty += (parseInt(c.newQty) || 0);
      acc.rentalQty += (parseInt(c.rentalQty) || 0);
      acc.repairableQty += (parseInt(c.repairableQty) || 0);
      acc.nonRepairableQty += (parseInt(c.nonRepairableQty) || 0);
      const rowTotalQty = (parseInt(c.newQty) || 0) + (parseInt(c.rentalQty) || 0) + (parseInt(c.repairableQty) || 0) + (parseInt(c.nonRepairableQty) || 0);
      acc.weight += rowTotalQty * (parseFloat(c.weight) || 0);
      return acc;
    }, { newQty: 0, rentalQty: 0, repairableQty: 0, nonRepairableQty: 0, weight: 0 });
  }, [formData.components]);

  const saveMic = async (status) => {
    if (!formData.micNo) return alert("MIC Number is required.");
    if (status === 'pending_qc' && !window.confirm("Are you sure you want to CONFIRM this MIC?")) return;

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        micStatus: status,
        totalWeight: totals.weight,
        updatedAt: serverTimestamp(),
        createdAt: micId ? formData.createdAt : serverTimestamp(),
      };

      if (micId) {
        await updateDoc(doc(db, "inwards", micId), payload);
      } else {
        await addDoc(collection(db, "inwards"), payload);
      }

      alert(`MIC ${status === 'draft' ? 'Saved as Draft' : 'Confirmed'} Successfully`);
      navigate('/ops/mic');
    } catch (error) {
      console.error("Error saving MIC:", error);
      alert("Error: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '25px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
            <ArrowLeft size={24} />
          </button>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ArrowDownLeft size={28} color="#2563eb" /> {micId ? 'Edit' : 'Create'} Material In Checklist
          </h2>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
            {isSaving && <Loader2 className="animate-spin" size={24} color="#2563eb" />}
            <button onClick={() => saveMic('draft')} disabled={isSaving} style={draftBtnStyle}><Save size={18}/> Save Draft</button>
            <button onClick={() => saveMic('pending_qc')} disabled={isSaving} style={confirmBtnStyle}><Send size={18}/> Confirm MIC</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
        <div style={cardStyle}>
          <h3 style={sectionHeaderStyle}>Document Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div><label style={labelStyle}>Date</label><input type="date" name="micDate" value={formData.micDate} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>MIC No.</label><input type="text" name="micNo" value={formData.micNo} onChange={handleChange} style={inputStyle} /></div>
            <div>
                <label style={labelStyle}>Type of MIC</label>
                <select name="typeOfMIC" value={formData.typeOfMIC} onChange={handleChange} style={inputStyle}>
                    <option value="">Select Type...</option>
                    <option value="Sale Return">Sale Return</option>
                    <option value="Rental">Rental</option>
                    <option value="Purchase">Purchase</option>
                    <option value="Interbranch Transfer">Interbranch Transfer</option>
                </select>
            </div>
            
            {/* Conditional Reference Inputs */}
            {formData.typeOfMIC && (
                <div style={{ gridColumn: 'span 2', position: 'relative' }} ref={pcSearchRef}>
                    <label style={labelStyle}>
                        {formData.typeOfMIC === 'Sale Return' || formData.typeOfMIC === 'Rental' ? 'Pickup Challan (PC) No.' : 
                         formData.typeOfMIC === 'Interbranch Transfer' ? 'Document No.' : 'Purchase Order No.'}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0 10px' }}>
                        <Search size={18} color="#64748b" />
                        <input 
                            type="text" 
                            name="referenceNo"
                            placeholder="Search or enter number..." 
                            value={pcSearchTerm} 
                            onChange={(e) => {
                                handleChange(e);
                                if (formData.typeOfMIC === 'Sale Return' || formData.typeOfMIC === 'Rental') handlePcSearch(e.target.value);
                            }} 
                            onFocus={() => (formData.typeOfMIC === 'Sale Return' || formData.typeOfMIC === 'Rental') && pcSearchTerm.length > 1 && setShowPcSuggestions(true)}
                            style={{ border: 'none', padding: '10px', outline: 'none', width: '100%', fontSize: '14px' }}
                        />
                        {fetchingPC && <Spinner size="small" />}
                    </div>
                    {showPcSuggestions && (
                        <div style={suggestionsBoxStyle}>
                            {pcSuggestions.map(pc => (
                                <div key={pc.id} onClick={() => handlePcSelection(pc)} style={suggestionItemStyle}>
                                    <div style={{ fontWeight: 'bold' }}>{pc.pcNo}</div>
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>{pc.customerName || pc.consigneeDetails?.split('\n')[0]}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
          </div>
        </div>

        <div style={cardStyle}>
          <h3 style={sectionHeaderStyle}>Consignee & Pickup Details</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div><label style={labelStyle}>Consignee (Customer Name)</label><input type="text" name="customerName" value={formData.customerName} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>Pickup From (Shipping Address)</label><textarea name="shippingAddress" value={formData.shippingAddress} onChange={handleChange} style={{ ...inputStyle, minHeight: '60px' }} /></div>
            <div>
              <label style={labelStyle}>Warehouse</label>
                <select name="warehouse" value={formData.warehouse} onChange={handleChange} style={inputStyle}>
                  <option value="">Select Warehouse...</option>
                  <option value="Jaipur">Jaipur</option>
                  <option value="Ahmedabad">Ahmedabad</option>
                  <option value="Bhiwandi">Bhiwandi</option>
                  <option value="New Delhi">New Delhi</option>
                  <option value="Hyderabad">Hyderabad</option>
                </select>
            </div>
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ margin: 0 }}>Component Table</h3>
          <div style={{ position: 'relative', width: '300px' }} ref={searchRef}>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#f3f4f6', padding: '8px 12px', borderRadius: '6px' }}>
              <Search size={16} color="#64748b" />
              <input 
                type="text" 
                placeholder="Search components to add..." 
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchTerm.length > 1 && setShowSuggestions(true)}
                style={{ border: 'none', background: 'transparent', marginLeft: '8px', outline: 'none', width: '100%', fontSize: '13px' }}
              />
            </div>
            {showSuggestions && (
              <div style={suggestionsBoxStyle}>
                {filteredProducts.map(p => (
                  <div key={p.id} onClick={() => addComponent(p)} style={suggestionItemStyle}>
                    <strong>{p.sku}</strong> - {p.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={{ ...thStyle, width: '50px' }}>S.No.</th>
                <th style={{ ...thStyle, width: '100px' }}>SKU</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Description</th>
                <th style={{ ...thStyle, width: '80px' }}>New</th>
                <th style={{ ...thStyle, width: '80px' }}>Rental</th>
                <th style={{ ...thStyle, width: '90px' }}>Repairable</th>
                <th style={{ ...thStyle, width: '110px' }}>Non Repairable</th>
                <th style={{ ...thStyle, width: '90px' }}>Weight (kg)</th>
                <th style={{ ...thStyle, width: '100px' }}>Total Weight</th>
                <th style={{ ...thStyle, width: '50px' }}></th>
              </tr>
            </thead>
            <tbody>
              {formData.components.map((c, index) => {
                const rowTotalQty = (parseInt(c.newQty || 0) + parseInt(c.rentalQty || 0) + parseInt(c.repairableQty || 0) + parseInt(c.nonRepairableQty || 0));
                const totalRowWeight = ( rowTotalQty * (parseFloat(c.weight) || 0) ).toFixed(2);
                
                return (
                  <tr key={c.id || index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>{c.sku}</td>
                    <td style={{ ...tdStyle, textAlign: 'left' }}>{c.name}</td>
                    <td style={tdStyle}>
                        <input type="number" value={c.newQty} onChange={(e) => handleComponentChange(index, 'newQty', e.target.value)} style={tableInputStyle} />
                    </td>
                    <td style={tdStyle}>
                        <input type="number" value={c.rentalQty} onChange={(e) => handleComponentChange(index, 'rentalQty', e.target.value)} style={tableInputStyle} />
                    </td>
                    <td style={tdStyle}>
                        <input type="number" value={c.repairableQty} onChange={(e) => handleComponentChange(index, 'repairableQty', e.target.value)} style={tableInputStyle} />
                    </td>
                    <td style={tdStyle}>
                        <input type="number" value={c.nonRepairableQty} onChange={(e) => handleComponentChange(index, 'nonRepairableQty', e.target.value)} style={tableInputStyle} />
                    </td>
                    <td style={tdStyle}>
                        <input type="number" step="0.01" value={c.weight} onChange={(e) => handleComponentChange(index, 'weight', e.target.value)} style={tableInputStyle} />
                    </td>
                    <td style={tdStyle}>{totalRowWeight}</td>
                    <td style={tdStyle}>
                      <button onClick={() => deleteComponent(index)} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer' }}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr style={{ backgroundColor: '#f9fafb', fontWeight: 'bold' }}>
                <td colSpan="3" style={{ ...tdStyle, textAlign: 'right' }}>TOTAL</td>
                <td style={tdStyle}>{totals.newQty}</td>
                <td style={tdStyle}>{totals.rentalQty}</td>
                <td style={tdStyle}>{totals.repairableQty}</td>
                <td style={tdStyle}>{totals.nonRepairableQty}</td>
                <td></td>
                <td style={tdStyle}>{totals.weight.toFixed(2)} kg</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
        <div style={cardStyle}>
            <h3 style={sectionHeaderStyle}><UserCheck size={18} /> Staff & Verification</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                    <label style={labelStyle}>Prepared By</label>
                    <select name="preparedBy" value={formData.preparedBy} onChange={handleChange} style={inputStyle}>
                        <option value="">Select...</option>
                        <option value="Rajesh">Rajesh</option>
                        <option value="Ghanshyam">Ghanshyam</option>
                        <option value="Ankit">Ankit</option>
                    </select>
                </div>
                <div>
                    <label style={labelStyle}>Quantity Verified By</label>
                    <select name="quantityVerifiedBy" value={formData.quantityVerifiedBy} onChange={handleChange} style={inputStyle}>
                        <option value="">Select...</option>
                        <option value="Prashant">Prashant</option>
                        <option value="Shakti">Shakti</option>
                        <option value="Govind">Govind</option>
                    </select>
                </div>
                <div style={{ gridColumn: 'span 2', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f3f4f6' }}>
                    <label style={labelStyle}><Truck size={14} /> Vehicle Details</label>
                </div>
                <div>
                    <label style={labelStyle}>Vehicle Number</label>
                    <input type="text" name="vehicleNumber" value={formData.vehicleNumber} onChange={handleChange} placeholder="e.g. RJ-14-GA-1234" style={inputStyle} />
                </div>
                <div>
                    <label style={labelStyle}>Date & Time</label>
                    <input type="datetime-local" name="vehicleDateTime" value={formData.vehicleDateTime} onChange={handleChange} style={inputStyle} />
                </div>
                <div>
                    <label style={labelStyle}>Vehicle In Time</label>
                    <input type="time" name="vehicleInTime" value={formData.vehicleInTime} onChange={handleChange} style={inputStyle} />
                </div>
                <div>
                    <label style={labelStyle}>Vehicle Out Time</label>
                    <input type="time" name="vehicleOutTime" value={formData.vehicleOutTime} onChange={handleChange} style={inputStyle} />
                </div>
            </div>
        </div>

        <div style={cardStyle}>
            <h3 style={sectionHeaderStyle}><ClipboardCheck size={18} /> Declaration & Acknowledgement</h3>
            <div style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '15px' }}>
                <p style={{ fontSize: '13px', color: '#475569', lineHeight: '1.5', margin: 0 }}>
                    "I hereby confirm that the material has been physically verified as per the Invoice and Challan. 
                    The received quantity matches the documents, and the quality has been checked and found satisfactory."
                </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                    <label style={labelStyle}>Verified By</label>
                    <select name="verifiedByFinance" value={formData.verifiedByFinance} onChange={handleChange} style={inputStyle}>
                        <option value="">Select...</option>
                        <option value="Shakti">Shakti</option>
                        <option value="Prashant">Prashant</option>
                        <option value="Govind">Govind</option>
                    </select>
                </div>
                <div>
                    <label style={labelStyle}>Date</label>
                    <input type="date" name="declarationDate" value={formData.declarationDate} onChange={handleChange} style={inputStyle} />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Signature / Verification Remark</label>
                    <input type="text" placeholder="Type name for digital signature..." style={inputStyle} />
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

const cardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const sectionHeaderStyle = { fontSize: '16px', fontWeight: 'bold', borderBottom: '1px solid #f3f4f6', paddingBottom: '10px', marginBottom: '15px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' };
const tableInputStyle = { width: '60px', textAlign: 'center', padding: '5px', borderRadius: '4px', border: '1px solid #d1d5db' };
const saveBtnStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 25px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' };
const draftBtnStyle = { ...saveBtnStyle, backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', display: 'flex', alignItems: 'center', gap: '8px' };
const confirmBtnStyle = { ...saveBtnStyle, backgroundColor: '#059669', display: 'flex', alignItems: 'center', gap: '8px' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '10px' };
const thStyle = { padding: '12px 10px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #f3f4f6' };
const tdStyle = { padding: '12px 10px', fontSize: '13px', color: '#374151', textAlign: 'center' };
const suggestionsBoxStyle = { position: 'absolute', top: '100%', left: 0, width: '100%', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', marginTop: '4px', zIndex: 10, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' };
const suggestionItemStyle = { padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '13px' };

export default CreateMICPage;